// Nachrichtenprotokoll zwischen Server und Client über WebSocket: versioniert und typisiert.
// Jede Nachricht trägt die Version `v` und den Typ `t`; die Prüfung geschieht von Hand mit
// Typwächtern, ohne Bibliothek. Ungültiges wird mit `null` abgewiesen, nie mit einer Ausnahme.
//
// Stand: Version 2 (docs/architektur-paket-2.md, Abschnitt „Protokoll v2“). Version 1 wird nicht
// mehr angenommen – eine alte, im Tab liegengebliebene Fassung des Clients bekommt so keine
// halben Antworten, sondern gar keine, und lädt beim nächsten Versuch neu.
//
// Die Nachrichten sind vollständig, obwohl der Server in task-010 nur `hello`, `clock` und
// `kicked` sendet: Die Datenformen sind der Vertrag, an dem die Tasks 011 bis 016 entlangbauen,
// und Typwächter für etwas, das erst später verschickt wird, kosten nichts.
import { AVATAR_BEREICHE, type AvatarColors } from '../avatar/types.js';
import type { AnimalState } from '../animals/behavior.js';
import type { ItemStack } from '../inventory/inventory.js';
import type { Vec3 } from '../sim/state.js';
import type { WorldParams } from '../world/parcels.js';
import { isPhraseIndex, type PhraseIndex } from './phrases.js';

/** Version des Protokolls; Nachrichten anderer Version werden abgewiesen. */
export const PROTOCOL_VERSION = 2 as const;
export type ProtocolVersion = typeof PROTOCOL_VERSION;

// ---------------------------------------------------------------------------------------------
// Datenformen, die in mehreren Nachrichten vorkommen
// ---------------------------------------------------------------------------------------------

/** Der eigene Spieler in der Begrüßung: Konto, Hof, Aussehen und der gespeicherte Spielstand. */
export interface YouInfo {
  /** Kennung des Kontos; sie ist im Protokoll die Kennung des Spielers. */
  id: number;
  name: string;
  /** Die eigene Parzelle oder null, solange keine vergeben ist. */
  parcelId: number | null;
  avatar: AvatarColors;
  /** Falsch, solange der Spieler den Spiegel noch nicht benutzt hat. */
  avatarChosen: boolean;
  pos: Vec3;
  yaw: number;
  flying: boolean;
  /** Zähler der eigenen Hof-Uhr in Hof-Sekunden, keine Uhrzeit. */
  farmSeconds: number;
}

/** Ein Mitspieler, wie ihn die Anwesenheitsliste und die Avatar-Ansicht brauchen. */
export interface PlayerInfo {
  id: number;
  name: string;
  avatar: AvatarColors;
  pos: Vec3;
  yaw: number;
  flying: boolean;
}

/** Ort und Haltung eines Mitspielers im 10-Hz-Takt; das Aussehen kam schon mit `PlayerInfo`. */
export interface PlayerPose {
  id: number;
  pos: Vec3;
  yaw: number;
  flying: boolean;
}

/** Besitzstand einer Parzelle: der Name des Besitzers steht auf dem Schild am Tor. */
export interface ParcelInfo {
  id: number;
  owner: string | null;
}

/** Der Zustand eines Hofs, wie der Server ihn verteilt. */
export interface FarmState {
  parcelId: number;
  /** Nur bei anwesendem Besitzer läuft die Hof-Uhr und bewegen sich die Tiere. */
  ownerPresent: boolean;
  gateOpen: boolean;
  /**
   * Stand der Hof-Uhr in Hof-Sekunden. Ohne ihn wüsste der Client nicht, wie er
   * `AnimalState.productReadyTick` lesen soll: Der zählt in Ticks **dieser** Uhr, und jeder Hof
   * hat seine eigene (ADR 0027).
   */
  farmSeconds: number;
  animals: AnimalState[];
  tree: { apples: number };
}

/** Ziel einer Interaktion: entweder ein Tier oder etwas Festes auf einer Parzelle. */
export type ParcelTargetKind = 'gate' | 'tree' | 'chest';
export type InteractTarget = { kind: 'animal'; id: string } | { kind: ParcelTargetKind; parcelId: number };

/** Woher ein Stapel beim Umlegen kommt: aus der Leiste (Inventar) oder aus der Kiste. */
export type ChestSide = 'leiste' | 'kiste';

// ---------------------------------------------------------------------------------------------
// Server → Client
// ---------------------------------------------------------------------------------------------

/** Erste Nachricht des Servers nach dem Verbinden: alles, was der Client zum Start braucht. */
export interface HelloMessage {
  v: ProtocolVersion;
  t: 'hello';
  worldId: string;
  /** Unix-Zeit in Millisekunden. */
  serverTime: number;
  params: WorldParams;
  you: YouInfo;
  parcels: ParcelInfo[];
  /** Die anderen Anwesenden, ohne einen selbst. */
  players: PlayerInfo[];
  farms: FarmState[];
  inventory: ItemStack[];
  /** Die Plätze der eigenen Kiste; ein leerer Platz ist null. */
  chest: (ItemStack | null)[];
}

/** Regelmäßige Uhr: Weltzeit für Tag und Nacht, dazu der Zähler der eigenen Hof-Uhr. */
export interface ClockMessage {
  v: ProtocolVersion;
  t: 'clock';
  /** Unix-Zeit in Millisekunden. */
  serverTime: number;
  farmSeconds: number;
}

/** Orte der Anwesenden im 10-Hz-Takt, ohne einen selbst. */
export interface PlayersMessage {
  v: ProtocolVersion;
  t: 'players';
  list: PlayerPose[];
}

export interface PlayerJoinedMessage {
  v: ProtocolVersion;
  t: 'playerJoined';
  player: PlayerInfo;
}

export interface PlayerLeftMessage {
  v: ProtocolVersion;
  t: 'playerLeft';
  id: number;
}

/** Zustand eines Hofs: bei Änderung und für belebte Höfe im Takt. */
export interface FarmMessage {
  v: ProtocolVersion;
  t: 'farm';
  farm: FarmState;
}

/**
 * Die Höfe eines Takts in einer Nachricht (task-016, ADR 0032): alle belebten Höfe fünfmal je Sekunde
 * und die, die sich im Takt geändert haben. Einzeln als `farm` hießen fünf anwesende Besitzer
 * fünfundzwanzig Nachrichten je Sekunde bei jedem Client; gebündelt bleibt es bei fünf.
 */
export interface FarmsMessage {
  v: ProtocolVersion;
  t: 'farms';
  list: FarmState[];
}

export interface InventoryMessage {
  v: ProtocolVersion;
  t: 'inventory';
  stacks: ItemStack[];
}

export interface ChestMessage {
  v: ProtocolVersion;
  t: 'chest';
  slots: (ItemStack | null)[];
}

/** Antwort auf eine Absicht: hat es geklappt, und wenn nicht, warum (deutscher Satz). */
export interface ActionResultMessage {
  v: ProtocolVersion;
  t: 'actionResult';
  ok: boolean;
  message?: string;
}

/** Ein Spieler hat einen der festen Sätze gesagt; der Text kommt vom Server. */
export interface SayMessage {
  v: ProtocolVersion;
  t: 'say';
  id: number;
  text: string;
}

/** Die Sitzung wurde woanders fortgesetzt; danach schließt der Server die Verbindung. */
export interface KickedMessage {
  v: ProtocolVersion;
  t: 'kicked';
  reason: 'anderswo';
}

/** Neuer Besitzer einer Parzelle; der Client schreibt das Schild um. */
export interface ParcelOwnerMessage {
  v: ProtocolVersion;
  t: 'parcelOwner';
  id: number;
  owner: string | null;
}

// ---------------------------------------------------------------------------------------------
// Client → Server
// ---------------------------------------------------------------------------------------------

/** Eigene Position und Blickrichtung, 10 Hz und nur bei Änderung. */
export interface MoveMessage {
  v: ProtocolVersion;
  t: 'move';
  pos: Vec3;
  yaw: number;
  pitch: number;
  flying: boolean;
}

/** Absicht, mit etwas im Blick zu handeln; der Server prüft Besitz, Nähe und Zustand. */
export interface InteractMessage {
  v: ProtocolVersion;
  t: 'interact';
  target: InteractTarget;
}

/** Verschiebt den ganzen Stapel am Platz `index` von `from` auf die andere Seite. */
export interface ChestMoveMessage {
  v: ProtocolVersion;
  t: 'chestMove';
  from: ChestSide;
  index: number;
}

/** Wunsch, einen der festen Sätze zu sagen. */
export interface SayRequestMessage {
  v: ProtocolVersion;
  t: 'say';
  phrase: PhraseIndex;
}

/** Tab versteckt oder wieder sichtbar; versteckt heißt sofort abwesend. */
export interface VisibilityMessage {
  v: ProtocolVersion;
  t: 'visibility';
  hidden: boolean;
}

/** Lebenszeichen des Clients; der Server antwortet mit `clock`. */
export interface PingMessage {
  v: ProtocolVersion;
  t: 'ping';
}

export type ServerMessage =
  | HelloMessage
  | ClockMessage
  | PlayersMessage
  | PlayerJoinedMessage
  | PlayerLeftMessage
  | FarmMessage
  | FarmsMessage
  | InventoryMessage
  | ChestMessage
  | ActionResultMessage
  | SayMessage
  | KickedMessage
  | ParcelOwnerMessage;

export type ClientMessage =
  | MoveMessage
  | InteractMessage
  | ChestMoveMessage
  | SayRequestMessage
  | VisibilityMessage
  | PingMessage;

export type Message = ServerMessage | ClientMessage;

// ---------------------------------------------------------------------------------------------
// Prüfung
// ---------------------------------------------------------------------------------------------

type Felder = Record<string, unknown>;

/** JSON lesen; alles, was kein Objekt (und kein Array) ergibt, gilt als ungültig. */
function parseFelder(raw: string): Felder | null {
  let wert: unknown;
  try {
    wert = JSON.parse(raw);
  } catch {
    return null;
  }
  return alsFelder(wert);
}

/** Ein einfaches Objekt oder null; Arrays und `null` gelten nicht als Objekt. */
function alsFelder(wert: unknown): Felder | null {
  if (typeof wert !== 'object' || wert === null || Array.isArray(wert)) return null;
  return wert as Felder;
}

function istVersion(felder: Felder): boolean {
  return felder['v'] === PROTOCOL_VERSION;
}

function istString(wert: unknown): wert is string {
  return typeof wert === 'string';
}

function istZahl(wert: unknown): wert is number {
  return typeof wert === 'number' && Number.isFinite(wert);
}

function istGanzzahl(wert: unknown): wert is number {
  return typeof wert === 'number' && Number.isInteger(wert);
}

function istBool(wert: unknown): wert is boolean {
  return typeof wert === 'boolean';
}

/** Kennung eines Kontos oder Spielers: ganze Zahl größer null. */
function istId(wert: unknown): wert is number {
  return istGanzzahl(wert) && wert > 0;
}

function leseVec3(wert: unknown): Vec3 | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { x, y, z } = felder;
  if (!istZahl(x) || !istZahl(y) || !istZahl(z)) return null;
  return { x, y, z };
}

const FARBE = /^#[0-9a-fA-F]{6}$/;

/**
 * Prüft die Form der Avatarfarben: vier Bereiche, je `#rrggbb`. Ob die Farben in der Palette
 * stehen, prüft `isAvatarColors` dort, wo der Spieler wählt (`PUT /api/avatar`, Spiegel) – nicht
 * hier: Eine später geänderte Palette darf gespeicherte Avatare nicht unlesbar machen.
 */
function leseAvatar(wert: unknown): AvatarColors | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  for (const bereich of AVATAR_BEREICHE) {
    const farbe = felder[bereich];
    if (!istString(farbe) || !FARBE.test(farbe)) return null;
  }
  // Nach der Schleife stehen in allen vier Bereichen Farben; übernommen werden nur diese vier.
  const { haut, haar, oberteil, hose } = felder as Record<keyof AvatarColors, string>;
  return { haut, haar, oberteil, hose };
}

function leseWorldParams(wert: unknown): WorldParams | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { parcels } = felder;
  if (!istGanzzahl(parcels) || parcels < 0) return null;
  return { parcels };
}

function leseItemStack(wert: unknown): ItemStack | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { item, count } = felder;
  if (!istString(item) || item === '' || !istGanzzahl(count) || count <= 0) return null;
  return { item, count };
}

function leseYou(wert: unknown): YouInfo | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { id, name, parcelId, avatarChosen, yaw, flying, farmSeconds } = felder;
  const avatar = leseAvatar(felder['avatar']);
  const pos = leseVec3(felder['pos']);
  if (!istId(id) || !istString(name) || avatar === null || pos === null) return null;
  if (parcelId !== null && !istId(parcelId)) return null;
  if (!istBool(avatarChosen) || !istZahl(yaw) || !istBool(flying) || !istZahl(farmSeconds)) return null;
  return { id, name, parcelId, avatar, avatarChosen, pos, yaw, flying, farmSeconds };
}

function lesePlayerInfo(wert: unknown): PlayerInfo | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { id, name, yaw, flying } = felder;
  const avatar = leseAvatar(felder['avatar']);
  const pos = leseVec3(felder['pos']);
  if (!istId(id) || !istString(name) || avatar === null || pos === null) return null;
  if (!istZahl(yaw) || !istBool(flying)) return null;
  return { id, name, avatar, pos, yaw, flying };
}

function lesePlayerPose(wert: unknown): PlayerPose | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { id, yaw, flying } = felder;
  const pos = leseVec3(felder['pos']);
  if (!istId(id) || pos === null || !istZahl(yaw) || !istBool(flying)) return null;
  return { id, pos, yaw, flying };
}

function leseParcelInfo(wert: unknown): ParcelInfo | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { id, owner } = felder;
  if (!istId(id)) return null;
  if (owner !== null && !istString(owner)) return null;
  return { id, owner };
}

function leseAnimalState(wert: unknown): AnimalState | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { id, species, yaw, idleTicks, penId, productReadyTick, moving, blockedTicks } = felder;
  const pos = leseVec3(felder['pos']);
  const zielRoh = felder['target'];
  const target = zielRoh === null ? null : leseVec3(zielRoh);
  if (!istString(id) || !istString(species) || pos === null || !istZahl(yaw)) return null;
  if (zielRoh !== null && target === null) return null;
  if (!istGanzzahl(idleTicks) || !istString(penId) || !istGanzzahl(productReadyTick)) return null;
  if (!istBool(moving) || !istGanzzahl(blockedTicks)) return null;
  return { id, species, pos, yaw, target, idleTicks, penId, productReadyTick, moving, blockedTicks };
}

function leseFarmState(wert: unknown): FarmState | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { parcelId, ownerPresent, gateOpen, farmSeconds } = felder;
  const animals = leseListe(felder['animals'], leseAnimalState);
  const baum = alsFelder(felder['tree']);
  if (!istId(parcelId) || !istBool(ownerPresent) || !istBool(gateOpen) || animals === null) return null;
  if (!istGanzzahl(farmSeconds) || farmSeconds < 0) return null;
  if (baum === null || !istGanzzahl(baum['apples']) || baum['apples'] < 0) return null;
  return { parcelId, ownerPresent, gateOpen, farmSeconds, animals, tree: { apples: baum['apples'] } };
}

function leseInteractTarget(wert: unknown): InteractTarget | null {
  const felder = alsFelder(wert);
  if (felder === null) return null;
  const { kind, id, parcelId } = felder;
  if (kind === 'animal') return istString(id) && id !== '' ? { kind, id } : null;
  if (kind === 'gate' || kind === 'tree' || kind === 'chest') {
    return istId(parcelId) ? { kind, parcelId } : null;
  }
  return null;
}

/** Liest eine Liste; ein einziger unbrauchbarer Eintrag verwirft die ganze Nachricht. */
function leseListe<T>(wert: unknown, lese: (eintrag: unknown) => T | null): T[] | null {
  if (!Array.isArray(wert)) return null;
  const liste: T[] = [];
  for (const eintrag of wert as unknown[]) {
    const gelesen = lese(eintrag);
    if (gelesen === null) return null;
    liste.push(gelesen);
  }
  return liste;
}

/** Wie `leseListe`, aber `null` ist hier ein gültiger Eintrag: ein leerer Platz in der Kiste. */
function leseSlots(wert: unknown): (ItemStack | null)[] | null {
  if (!Array.isArray(wert)) return null;
  const slots: (ItemStack | null)[] = [];
  for (const eintrag of wert as unknown[]) {
    if (eintrag === null) {
      slots.push(null);
      continue;
    }
    const stapel = leseItemStack(eintrag);
    if (stapel === null) return null;
    slots.push(stapel);
  }
  return slots;
}

/**
 * Liest eine Nachricht des Servers. `null` bei ungültigem JSON, falscher Version, unbekanntem Typ,
 * fehlenden oder falsch typisierten Feldern. Unbekannte Zusatzfelder werden ignoriert; das
 * Ergebnis enthält nur die bekannten Felder und ist stets ein frisches Objekt.
 */
export function parseServerMessage(raw: string): ServerMessage | null {
  const felder = parseFelder(raw);
  if (felder === null || !istVersion(felder)) return null;
  const v = PROTOCOL_VERSION;
  const { t } = felder;

  switch (t) {
    case 'hello': {
      const { worldId, serverTime } = felder;
      const params = leseWorldParams(felder['params']);
      const you = leseYou(felder['you']);
      const parcels = leseListe(felder['parcels'], leseParcelInfo);
      const players = leseListe(felder['players'], lesePlayerInfo);
      const farms = leseListe(felder['farms'], leseFarmState);
      const inventory = leseListe(felder['inventory'], leseItemStack);
      const chest = leseSlots(felder['chest']);
      if (!istString(worldId) || !istZahl(serverTime) || params === null || you === null) return null;
      if (parcels === null || players === null || farms === null || inventory === null || chest === null) return null;
      return { v, t, worldId, serverTime, params, you, parcels, players, farms, inventory, chest };
    }
    case 'clock': {
      const { serverTime, farmSeconds } = felder;
      if (!istZahl(serverTime) || !istGanzzahl(farmSeconds)) return null;
      return { v, t, serverTime, farmSeconds };
    }
    case 'players': {
      const list = leseListe(felder['list'], lesePlayerPose);
      return list === null ? null : { v, t, list };
    }
    case 'playerJoined': {
      const player = lesePlayerInfo(felder['player']);
      return player === null ? null : { v, t, player };
    }
    case 'playerLeft': {
      const { id } = felder;
      return istId(id) ? { v, t, id } : null;
    }
    case 'farm': {
      const farm = leseFarmState(felder['farm']);
      return farm === null ? null : { v, t, farm };
    }
    case 'farms': {
      const list = leseListe(felder['list'], leseFarmState);
      return list === null ? null : { v, t, list };
    }
    case 'inventory': {
      const stacks = leseListe(felder['stacks'], leseItemStack);
      return stacks === null ? null : { v, t, stacks };
    }
    case 'chest': {
      const slots = leseSlots(felder['slots']);
      return slots === null ? null : { v, t, slots };
    }
    case 'actionResult': {
      const { ok, message } = felder;
      if (!istBool(ok)) return null;
      if (message === undefined) return { v, t, ok };
      return istString(message) ? { v, t, ok, message } : null;
    }
    case 'say': {
      const { id, text } = felder;
      return istId(id) && istString(text) ? { v, t, id, text } : null;
    }
    case 'kicked': {
      return felder['reason'] === 'anderswo' ? { v, t, reason: 'anderswo' } : null;
    }
    case 'parcelOwner': {
      const { id, owner } = felder;
      if (!istId(id)) return null;
      if (owner !== null && !istString(owner)) return null;
      return { v, t, id, owner };
    }
    default:
      return null;
  }
}

/** Liest eine Nachricht des Clients; Regeln wie bei `parseServerMessage`. */
export function parseClientMessage(raw: string): ClientMessage | null {
  const felder = parseFelder(raw);
  if (felder === null || !istVersion(felder)) return null;
  const v = PROTOCOL_VERSION;
  const { t } = felder;

  switch (t) {
    case 'move': {
      const { yaw, pitch, flying } = felder;
      const pos = leseVec3(felder['pos']);
      if (pos === null || !istZahl(yaw) || !istZahl(pitch) || !istBool(flying)) return null;
      return { v, t, pos, yaw, pitch, flying };
    }
    case 'interact': {
      const target = leseInteractTarget(felder['target']);
      return target === null ? null : { v, t, target };
    }
    case 'chestMove': {
      const { from, index } = felder;
      if (from !== 'leiste' && from !== 'kiste') return null;
      if (!istGanzzahl(index) || index < 0) return null;
      return { v, t, from, index };
    }
    case 'say': {
      const { phrase } = felder;
      return isPhraseIndex(phrase) ? { v, t, phrase } : null;
    }
    case 'visibility': {
      const { hidden } = felder;
      return istBool(hidden) ? { v, t, hidden } : null;
    }
    case 'ping':
      return { v, t };
    default:
      return null;
  }
}

/** Serialisiert eine Nachricht für den Versand über WebSocket. */
export function encodeMessage(msg: Message): string {
  return JSON.stringify(msg);
}
